import React, { useState, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useChannel } from "./hooks/useChannel";
import Login from "./components/Login";
import Overview from "./components/Overview";
import UpgradeModal from "./components/UpgradeModal";
import OnboardingModal from "./components/OnboardingModal";
import { ActivityFeed, useActivityLogger } from "./components/ActivityFeed";
import { AddChannelModal, FetchConfirmModal } from "./components/Modals";

// ── Lazy-loaded tab components ────────────────────────────────────────────────
// These are never needed on the initial render — each loads on first navigation
// to that tab, then stays cached. Saves ~121KB from the initial bundle.
const Episodes            = React.lazy(() => import("./components/PageComponents").then(m => ({ default: m.Episodes })));
const ClipTracker         = React.lazy(() => import("./components/PageComponents").then(m => ({ default: m.ClipTracker })));
const Platform            = React.lazy(() => import("./components/PageComponents").then(m => ({ default: m.Platform })));
const Branding            = React.lazy(() => import("./components/PageComponents").then(m => ({ default: m.Branding })));
const BrandingGuide       = React.lazy(() => import("./components/BrandingGuide"));
const ClassifyReview      = React.lazy(() => import("./components/ClassifyReview"));
const ClassificationSettings = React.lazy(() => import("./components/ClassificationSettings"));
const AdminPanel          = React.lazy(() => import("./components/AdminPanel"));
const ChannelOAuthSection = React.lazy(() => import("./components/AdminPanel").then(m => ({ default: m.ChannelOAuthSection })));
const PlatformConnections = React.lazy(() => import("./components/AdminPanel").then(m => ({ default: m.PlatformConnections })));
const QuotaDashboard      = React.lazy(() => import("./components/QuotaDashboard"));
const BillingPanel        = React.lazy(() => import("./components/BillingPanel"));
const TermsPage           = React.lazy(() => import("./components/TermsPage"));
const PrivacyPage         = React.lazy(() => import("./components/PrivacyPage"));
const AnalyticsDashboard  = React.lazy(() => import("./components/AnalyticsDashboard"));
const ContentCalendar     = React.lazy(() => import("./components/ContentCalendar"));
const Connections         = React.lazy(() => import("./components/Connections"));
const ClipJobStatus       = React.lazy(() => import("./components/ClipJobStatus"));
const ChannelDefaults     = React.lazy(() => import("./components/ChannelDefaults"));
const SponsorshipTracker  = React.lazy(() => import("./components/SponsorshipTracker"));
const AdminFeedback       = React.lazy(() => import("./components/AdminFeedback"));
// eslint-disable-next-line no-unused-vars
const FeedbackModal       = React.lazy(() => import("./components/FeedbackModal"));

// ── Suspense fallback — matches app loading style ─────────────────────────────
const TabFallback = () => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"40vh", color:"#5A6A7E", fontFamily:"Inter,sans-serif", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.2em" }}>
    Loading...
  </div>
);

// ── Hex badge logo mark ───────────────────────────────────────────────────────
const LogoMark = ({ size = 36 }) => (
  <svg viewBox="0 0 80 80" width={size} height={size} xmlns="http://www.w3.org/2000/svg" style={{ display:"inline-block", verticalAlign:"middle", flexShrink:0 }}>
    <polygon points="40,3 72,21 72,59 40,77 8,59 8,21" fill="none" stroke="#C9A227" strokeWidth="5"/>
    <polygon points="40,10 65,25 65,55 40,70 15,55 15,25" fill="#080C12" stroke="#2B5EA7" strokeWidth="1.5"/>
    <line x1="17" y1="40" x2="29" y2="40" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="51" y1="40" x2="63" y2="40" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="40" y1="13" x2="40" y2="25" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <line x1="40" y1="55" x2="40" y2="67" stroke="#2B5EA7" strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="40" cy="40" r="14" fill="none" stroke="#2B5EA7" strokeWidth="1.5"/>
    <circle cx="40" cy="40" r="6" fill="#D93025"/>
    <polygon points="34,34 34,46 48,40" fill="#E8EBF0"/>
    <ellipse cx="40" cy="14" rx="7" ry="3" fill="#C9A227"/>
    <ellipse cx="40" cy="11.5" rx="4" ry="2.5" fill="#C9A227"/>
  </svg>
);

// ── Nav config by role ────────────────────────────────────────────────────────
const getTabsForRole = (role) => {
  const common = [
    { id:"overview",  label:"Overview",      icon:"◎" },
    { id:"episodes",  label:"Episodes",      icon:"▤" },
    { id:"clips",     label:"Clip Tracker",  icon:"✦" },
    { id:"clip-jobs",  label:"Clip Jobs",     icon:"▶" },
    { id:"platform",  label:"Platform Plan", icon:"⊞" },
    { id:"branding",  label:"Branding",      icon:"◈" },
    { id:"analytics",  label:"Analytics",     icon:"◉" },
    { id:"calendar",   label:"Calendar",      icon:"⊞" },
    { id:"connections",label:"Connections",   icon:"⊕" },
    { id:"settings",   label:"Settings",      icon:"⚙" },
    { id:"sponsors",   label:"Sponsors",      icon:"◇" },
  ];
  const adminOwner = [
    { id:"branding-guide", label:"Brand Guide", icon:"✦" },
    { id:"classify",       label:"Classify",    icon:"◐" },
  ];
  const adminOnly = [
    { id:"quota",    label:"Quota & Limits",  icon:"⬡" },
    { id:"admin",    label:"Team Access",     icon:"⊛" },
    { id:"feedback-admin", label:"Feedback", icon:"◉" },
  ];
  if (role === "admin") return [...common, ...adminOwner, ...adminOnly];
  if (role === "owner") return [...common, ...adminOwner];
  return common; // user: read-only, no extra tabs
};

// ── Role badge ────────────────────────────────────────────────────────────────
const RolePill = ({ role }) => {
  const map = {
    admin: { label:"Admin", color:"#C9A227" },
    owner: { label:"Owner", color:"#2B5EA7" },
    user:  { label:"User",  color:"#5A6A7E" },
  };
  const r = map[role] || map.user;
  return (
    <span style={{ padding:"1px 7px", border:`1px solid ${r.color}`, borderRadius:2, background:`${r.color}22`, color:r.color, fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em" }}>
      {r.label}
    </span>
  );
};

function MainApp() {
  const { user, userRole, logout, loading: authLoading, isAdmin, canWrite } = useAuth();
  const ch = useChannel(user, userRole);
  const [tab, setTab] = useState("overview");
  const [syncing, setSyncing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [showFeedback, setShowFeedback] = useState(false);

  const [detail, setDetail] = useState(null);

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  React.useEffect(() => {
    const handler = (e) => {
      // Skip if user is typing in an input/textarea
      if (["INPUT","TEXTAREA","SELECT"].includes(e.target.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch(e.key) {
        case "Escape":
          if (detail) { setDetail(null); e.preventDefault(); }
          else if (sidebarOpen) { setSidebarOpen(false); e.preventDefault(); }
          break;
        case "1": setTab("overview");     setDetail(null); break;
        case "2": setTab("episodes");     setDetail(null); break;
        case "3": setTab("clips");        setDetail(null); break;
        case "4": setTab("platform");     setDetail(null); break;
        case "5": setTab("analytics");    setDetail(null); break;
        case "6": setTab("calendar");     setDetail(null); break;
        case "7": setTab("connections");  setDetail(null); break;
        default: break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
// eslint-disable-next-line no-use-before-define
  }, [detail, sidebarOpen, setDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  const [upgradeReason, setUpgradeReason] = useState(null);
  const logActivity = useActivityLogger(ch.activeId, user); // eslint-disable-line no-unused-vars

  // Global AI credit error handler
  React.useEffect(() => {
    const handler = (e) => {
      if (e.reason?.message === "NO_CREDITS" || e.message === "NO_CREDITS") {
        setUpgradeReason("no_credits");
      }
    };
    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  // Handle Stripe checkout redirect
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get('checkout');
    if (checkout === 'success') {
      alert('🎉 Welcome to Creator Commandos! Your subscription is active. Credits will be available shortly.');
      window.history.replaceState({}, '', '/');
    } else if (checkout === 'cancelled') {
      window.history.replaceState({}, '', '/');
    }
  }, []);
  const [legalModal, setLegalModal] = useState(null); // "tos" | "privacy" | null

  const [classifySubTab, setClassifySubTab] = useState("review");
  const [showAdd, setShowAdd] = useState(false);
  const [fetchConfirm, setFetchConfirm] = useState(null);

  if (authLoading) return (
    <div style={{ minHeight:"100vh", background:"#080C12", display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <LogoMark size={52}/>
      <div style={{ color:"#5A6A7E", fontFamily:"Inter,sans-serif", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.2em" }}>Loading...</div>
    </div>
  );

  if (!user) return <Login/>;

  const TABS = getTabsForRole(userRole?.role || "user");
  const active = ch.channels.find(c => c.id === ch.activeId);

  const handleAdd = async (name, ytId, apiKey, channelType) => {
    const result = await ch.addChannel(name, ytId, apiKey, channelType);
    if (result) { setShowAdd(false); setFetchConfirm(result); }
  };

  const handleFetch = async (limit) => {
    if (!fetchConfirm) return;
    setFetchConfirm(null);
    await ch.executeFetch(fetchConfirm.channel, fetchConfirm.uploadsId, limit);
  };

  const tabProps = { ch, detail, setDetail, tab, setTab };


  const LegalModal = ({ type, onClose }) => {
    const isTos = type === "tos";
    return (
      <div style={{position:"fixed",inset:0,background:"rgba(8,12,18,0.92)",zIndex:1000,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"40px 16px",overflowY:"auto"}} onClick={onClose}>
        <div style={{background:"#0D1825",border:"1px solid #1A2940",borderTop:"3px solid #C9A227",borderRadius:4,padding:32,width:"100%",maxWidth:720,position:"relative"}} onClick={e=>e.stopPropagation()}>
          <button onClick={onClose} style={{position:"absolute",top:16,right:16,background:"none",border:"none",color:"#5A6A7E",cursor:"pointer",fontSize:20,lineHeight:1}}>✕</button>
          <div style={{fontSize:11,color:"#C9A227",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.14em",marginBottom:8}}>Creator Commandos</div>
          <h2 style={{fontSize:22,fontWeight:700,color:"#E8EBF0",marginBottom:4}}>{isTos ? "Terms of Service" : "Privacy Policy"}</h2>
          <div style={{fontSize:11,color:"#5A6A7E",marginBottom:24}}>Last updated: April 2026 · These are placeholder documents — have an attorney review before charging customers.</div>
          <div style={{fontSize:13,color:"#94a3b8",lineHeight:1.8,maxHeight:"60vh",overflowY:"auto"}}>
            {isTos ? (
              <>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>1. Acceptance of Terms</strong><br/>By accessing Creator Commandos ("the Service"), you agree to these Terms. If you disagree, please do not use the Service.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>2. Description of Service</strong><br/>Creator Commandos is a YouTube channel management platform providing tools for content analysis, clip planning, transcript management, and AI-assisted content suggestions.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>3. Eligibility</strong><br/>You must be at least 18 years old to use this Service.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>4. Account Responsibilities</strong><br/>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>5. YouTube API Services</strong><br/>Creator Commandos uses YouTube API Services. By using the Service, you agree to the YouTube Terms of Service (youtube.com/t/terms).</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>6. AI Features</strong><br/>AI-generated content is provided for informational purposes only. You are responsible for reviewing any AI-generated content before use.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>7. Intellectual Property</strong><br/>Your YouTube channel data and content remain your property. The Service and its original content remain the exclusive property of Creator Commandos.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>8. Limitation of Liability</strong><br/>To the maximum extent permitted by law, Creator Commandos shall not be liable for any indirect, incidental, special, consequential, or punitive damages.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>9. Termination</strong><br/>We reserve the right to terminate or suspend your account at our discretion for conduct that violates these Terms.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>10. Contact</strong><br/>Questions about these Terms: hello@creatorcommandos.app</p>
              </>
            ) : (
              <>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>1. Information We Collect</strong><br/>We collect Google account information (name, email) via OAuth sign-in, YouTube channel data you connect, transcripts and notes you enter, and usage data.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>2. How We Use Your Information</strong><br/>To provide and improve the Service, generate AI-powered content suggestions, display your channel analytics, and communicate with you about the Service.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>3. Data Storage</strong><br/>Your data is stored in Google Firebase (Firestore), located in the United States. We use industry-standard security measures.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>4. Third-Party Services</strong><br/>We use Google Firebase (database/auth), Netlify (hosting), Anthropic (AI generation), YouTube Data API, and Stripe (payments).</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>5. YouTube API Data</strong><br/>Data obtained through YouTube API Services is used only to provide the features of Creator Commandos. You can revoke access at myaccount.google.com/permissions.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>6. Data Sharing</strong><br/>We do not sell your personal information. We share data only with service providers who help us operate the Service, or when required by law.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>7. Your Rights</strong><br/>You have the right to access, correct, or request deletion of your data. Contact us at hello@creatorcommandos.app.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>8. Security</strong><br/>We implement HTTPS encryption, Firebase security rules, and server-side API key management.</p>
                <p style={{marginBottom:16}}><strong style={{color:"#E8EBF0"}}>9. Contact</strong><br/>Privacy questions: hello@creatorcommandos.app</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight:"100vh", background:"#080C12", backgroundImage:"linear-gradient(#0D1825 1px,transparent 1px),linear-gradient(90deg,#0D1825 1px,transparent 1px)", backgroundSize:"48px 48px", fontFamily:"Inter,system-ui,sans-serif", color:"#E8EBF0" }}>
      {/* Mobile header */}
      <div className="cc-mobile-header" style={{zIndex:110}}>
        <button className={`cc-hamburger${sidebarOpen?" open":""}`} onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={sidebarOpen?"Close menu":"Open menu"}><span className="cc-hbar"></span><span className="cc-hbar"></span><span className="cc-hbar"></span></button>
        <span style={{fontSize:11,fontWeight:700,color:"#C9A227",textTransform:"uppercase",letterSpacing:"0.14em"}}>Creator Commandos</span>
        <div style={{width:32}}/>
      </div>
      {/* Sidebar overlay */}
      <div className={`cc-sidebar-overlay${sidebarOpen?" open":""}`} onClick={() => setSidebarOpen(false)}/>
      <div className="cc-shell" style={{display:"flex",minHeight:"100vh"}}>

      {/* ── Modals ── */}
      {showAdd && isAdmin && <AddChannelModal onAdd={handleAdd} onClose={() => setShowAdd(false)} loading={ch.loading}/>}
      {fetchConfirm && isAdmin && <FetchConfirmModal data={fetchConfirm} currentCount={ch.videos.length} onConfirm={handleFetch} onClose={() => setFetchConfirm(null)}/>}

      {/* ── Sidebar ── */}
      <div className={`cc-sidebar${sidebarOpen ? " open" : ""}`} style={{
          width: 224,
          background: "#080C12",
          borderRight: "1px solid #1A2940",
          flexDirection: "column",
          overflowY: "auto",
        }}>
        <div style={{ height:3, background:"#C9A227", flexShrink:0 }}/>

        {/* Logo */}
        <div style={{ padding:"18px 16px 14px", borderBottom:"1px solid #1A2940" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
            <LogoMark size={34}/>
            <div>
              <div style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:15, letterSpacing:"0.08em", textTransform:"uppercase", color:"#E8EBF0", lineHeight:1.1 }}>Creator</div>
              <div style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:15, letterSpacing:"0.08em", textTransform:"uppercase", color:"#C9A227", lineHeight:1.1 }}>Commandos</div>
            </div>
          </div>
          <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.2em", color:"#5A6A7E", marginLeft:44 }}>Manage Your Mission</div>
        </div>

        {/* Channels */}
        <div style={{ padding:"12px 16px 10px", borderBottom:"1px solid #1A2940" }}>
          <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.2em", color:"#5A6A7E", marginBottom:8 }}>
            {isAdmin ? "All Channels" : "Your Channel"}
          </div>
          {ch.channels.map(c => (
            <button key={c.id} onClick={() => { ch.setActiveId(c.id); setDetail(null); setSidebarOpen(false); }}
              style={{ display:"block", width:"100%", padding:"6px 10px", border:"1px solid transparent", borderRadius:3, background:c.id===ch.activeId?"#0D1825":"none", color:c.id===ch.activeId?"#C9A227":"#5A6A7E", fontFamily:"inherit", fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.08em", cursor:"pointer", textAlign:"left", marginBottom:3, borderColor:c.id===ch.activeId?"#1A2940":"transparent", transition:"all 0.12s" }}>
              {c.id===ch.activeId && <span style={{ color:"#D93025", marginRight:6 }}>▸</span>}
              <span style={{ flex:1 }}>{c.name}</span>
              {c.channelType && c.channelType !== "main" && (
                <span style={{ fontSize:9, padding:"1px 5px", borderRadius:2,
                  background: c.channelType==="clips"?"rgba(217,48,37,0.15)":c.channelType==="podcast"?"rgba(201,162,39,0.15)":c.channelType==="shorts"?"rgba(52,211,153,0.15)":"rgba(90,106,126,0.15)",
                  color: c.channelType==="clips"?"#D93025":c.channelType==="podcast"?"#C9A227":c.channelType==="shorts"?"#34d399":"#5A6A7E",
                  fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", flexShrink:0 }}>
                  {c.channelType==="clips"?"Clips":c.channelType==="podcast"?"Pod":c.channelType==="shorts"?"Shorts":c.channelType}
                </span>
              )}
            </button>
          ))}
          {/* Only admins can add channels */}
          {isAdmin && (
            <button onClick={() => { setShowAdd(true); setSidebarOpen(false); }}
              style={{ display:"flex", alignItems:"center", gap:6, width:"100%", padding:"6px 10px", border:"1px dashed #1A2940", borderRadius:3, background:"none", color:"#2B5EA7", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", cursor:"pointer", marginTop:6 }}
              onMouseOver={e => { e.currentTarget.style.borderColor="#2B5EA7"; e.currentTarget.style.color="#C9A227"; }}
              onMouseOut={e => { e.currentTarget.style.borderColor="#1A2940"; e.currentTarget.style.color="#2B5EA7"; }}>
              + Add Channel
            </button>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex:1, padding:"8px 0", overflowY:"auto" }}>
          <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.2em", color:"#5A6A7E", padding:"8px 16px 4px" }}>Navigation</div>
          {TABS.map(({ id, label, icon }) => {
            const isActive = tab === id;
            const isAdminTab = id === "admin";
            return (
              <button key={id} onClick={() => { setTab(id); setDetail(null); setSidebarOpen(false); }}
                style={{ display:"flex", alignItems:"center", gap:9, width:"100%", padding:"9px 18px", border:"none", borderLeft:`3px solid ${isActive?(isAdminTab?"#C9A227":"#D93025"):"transparent"}`, background:isActive?"#0D1825":"none", color:isActive?(isAdminTab?"#C9A227":"#C9A227"):"#5A6A7E", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", cursor:"pointer", textAlign:"left", transition:"all 0.12s" }}
                onMouseOver={e => { if(!isActive){ e.currentTarget.style.background="#0D1825"; e.currentTarget.style.color="#E8EBF0"; e.currentTarget.style.borderLeftColor="#2B5EA7"; }}}
                onMouseOut={e => { if(!isActive){ e.currentTarget.style.background="none"; e.currentTarget.style.color="#5A6A7E"; e.currentTarget.style.borderLeftColor="transparent"; }}}>
                <span style={{ fontSize:13, opacity:0.7, flexShrink:0 }}>{icon}</span>
                {label}
                {isActive && <span style={{ marginLeft:"auto", width:4, height:4, background:isAdminTab?"#C9A227":"#D93025", borderRadius:"50%", flexShrink:0 }}/>}
              </button>
            );
          })}
        </nav>

        {/* User + sign out */}
        <div style={{ borderTop:"1px solid #1A2940" }}>
          {active && canWrite && (
            <div style={{ padding:"10px 16px", borderBottom:"1px solid #1A2940" }}>
              <button onClick={ch.manualRefresh} disabled={ch.loading}
                style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"7px 10px", border:"1px solid #1A2940", borderRadius:3, background:"none", color:ch.loading?"#5A6A7E":"#2B5EA7", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", cursor:ch.loading?"default":"pointer", opacity:ch.loading?0.5:1 }}>
                <span style={{ fontSize:14 }}>↺</span> {ch.loading ? "Syncing..." : "Refresh Now"}
              </button>
              <button onClick={async () => {
                setSyncing(true);
                await ch.resyncPlaylists();
                setSyncing(false);
              }} disabled={syncing || ch.loading}
                style={{ display:"flex", alignItems:"center", gap:8, width:"100%", padding:"7px 10px", marginTop:6, border:"1px solid #1A2940", borderRadius:3, background:"none", color:syncing?"#5A6A7E":"#C9A227", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", cursor:syncing||ch.loading?"default":"pointer", opacity:syncing?0.5:1 }}>
                <span style={{ fontSize:12 }}>⬡</span> {syncing ? "Syncing playlists..." : "Re-sync Playlists"}
              </button>
            </div>
          )}
          <div style={{ padding:"10px 16px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:5 }}>
              <div style={{ fontSize:10, color:"#5A6A7E", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontWeight:600, flex:1 }}>{user.email}</div>
              <RolePill role={userRole?.role}/>
            </div>
            <div style={{display:"flex",gap:12,marginBottom:8}}>
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{color:"#5A6A7E",fontFamily:"inherit",fontSize:9,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.1em",textDecoration:"none"}}>Terms</a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color:"#5A6A7E",fontFamily:"inherit",fontSize:9,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.1em",textDecoration:"none"}}>Privacy</a>
            </div>
            <div style={{ display:"flex", gap:12, alignItems:"center" }}>
              <button onClick={() => setShowFeedback(true)}
                style={{ background:"none", border:"1px solid #1A2940", borderRadius:3, color:"#5A6A7E", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", cursor:"pointer", padding:"5px 10px" }}
                onMouseOver={e => { e.currentTarget.style.borderColor="#C9A227"; e.currentTarget.style.color="#C9A227"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor="#1A2940"; e.currentTarget.style.color="#5A6A7E"; }}>
                ◈ Feedback
              </button>
              <button onClick={logout}
                style={{ background:"none", border:"none", color:"#5A6A7E", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", cursor:"pointer", padding:0 }}
                onMouseOver={e => e.currentTarget.style.color="#D93025"}
                onMouseOut={e => e.currentTarget.style.color="#5A6A7E"}>
                Sign Out
              </button>
            </div>
          </div>
        </div>
        <div style={{ height:3, background:"#D93025", flexShrink:0 }}/>
      </div>

      {/* ── Main ── */}
      <div className="cc-main" style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>

        {/* Header */}
        <div style={{ background:"#080C12", borderBottom:"1px solid #1A2940", position:"sticky", top:0, zIndex:10 }}>
          <div style={{ height:3, background:"#D93025" }}/>
          <div className="cc-channel-header-inner" style={{ padding:"12px 24px", display:"flex", alignItems:"center", gap:16 }}>
            <div>
              {active ? (
                <>
                  <div className="cc-channel-name" style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:16, letterSpacing:"0.06em", textTransform:"uppercase", color:"#E8EBF0", lineHeight:1 }}>{active.name}</div>
                  <div className="cc-channel-sync" style={{ fontSize:10, color:"#5A6A7E", marginTop:3, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                    {ch.refreshLog.lastAutoRefresh ? `Last sync: ${new Date(ch.refreshLog.lastAutoRefresh).toLocaleString()}` : "Not yet synced"}
                  </div>
                </>
              ) : (
                <div style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:16, letterSpacing:"0.06em", textTransform:"uppercase", color:"#5A6A7E" }}>No Channel Selected</div>
              )}
            </div>
            <div style={{ flex:1 }}/>
            {/* Read-only indicator for users */}
            {!canWrite && (
              <div style={{ padding:"5px 12px", border:"1px solid #5A6A7E", borderRadius:3, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#5A6A7E" }}>
                👁 Read Only
              </div>
            )}
            {active && isAdmin && ch.videos.length > 0 && (
              <div className="cc-header-stats" style={{ display:"flex", gap:20, alignItems:"center" }}>
                {[
                  { label:"Videos", value:ch.videos.length.toLocaleString() },
                  { label:"Reviewed", value:`${ch.videos.filter(v=>ch.tracking[v.id]?.reviewed).length}/${ch.videos.length}` },
                  { label:"Clips", value:Object.values(ch.clips).flat().length },
                ].map(({ label, value }) => (
                  <div key={label} style={{ textAlign:"center" }}>
                    <div style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:16, color:"#C9A227", letterSpacing:"0.02em", lineHeight:1 }}>{value}</div>
                    <div style={{ fontSize:9, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.16em", color:"#5A6A7E", marginTop:2 }}>{label}</div>
                  </div>
                ))}
                <div style={{ width:1, height:28, background:"#1A2940" }}/>
              </div>
            )}
            {isAdmin && (
              <button className="cc-header-add-btn" onClick={() => setShowAdd(true)}
                style={{ padding:"7px 16px", border:"1px solid #C9A227", borderRadius:3, background:"none", color:"#C9A227", fontFamily:"inherit", fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", cursor:"pointer" }}
                onMouseOver={e => { e.currentTarget.style.background="#C9A227"; e.currentTarget.style.color="#080C12"; }}
                onMouseOut={e => { e.currentTarget.style.background="none"; e.currentTarget.style.color="#C9A227"; }}>
                + Add Channel
              </button>
            )}
          </div>
          <div style={{ height:2, background:"linear-gradient(90deg,#C9A227,transparent)" }}/>
        </div>

        {/* Status bars */}
        {ch.loading && (
          <div style={{ background:"rgba(43,94,167,0.08)", borderBottom:"1px solid #2B5EA7", padding:"8px 24px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em", color:"#2B5EA7" }}>
            ⟳ {ch.loadPhase==="channel-info" ? "Acquiring channel data..." : ch.loadPhase==="videos" ? `Downloading${ch.progress.total?` — ${ch.progress.n.toLocaleString()} of ${ch.progress.total.toLocaleString()}`:"..."}` : "Loading..."}
          </div>
        )}
        {ch.quotaBlocked && (
          <div style={{ background:"rgba(201,162,39,0.08)", borderBottom:"1px solid #C9A227", padding:"8px 24px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#C9A227", display:"flex", gap:10, alignItems:"center" }}>
            <span>⬡</span>
            <span style={{ flex:1 }}>Firestore daily read quota reached — serving from local cache. Data may be up to 1 hour old. Upgrade to Blaze plan to remove this limit.</span>
          </div>
        )}
        {ch.error && (
          <div style={{ background:"rgba(217,48,37,0.08)", borderBottom:"1px solid #D93025", padding:"8px 24px", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"#D93025", display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ flex:1 }}>⚠ {ch.error}</span>
            <button onClick={() => ch.setError(null)} style={{ background:"none", border:"none", color:"#D93025", cursor:"pointer", fontSize:18 }}>×</button>
          </div>
        )}

        {/* Content */}
        <div className="cc-content" style={{ flex:1, padding:24, overflowY:"auto" }}>
          <Suspense fallback={<TabFallback />}>
          {/* Admin panel doesn't need an active channel */}
          {tab === "admin" && isAdmin ? (
            <div>
              <div style={{ fontFamily:"Impact,'Arial Black',sans-serif", fontSize:20, textTransform:"uppercase", letterSpacing:"0.08em", color:"#E8EBF0", marginBottom:4 }}>Team Access</div>
              <div style={{ fontSize:12, color:"#5A6A7E", marginBottom:20, textTransform:"uppercase", letterSpacing:"0.1em", fontWeight:600 }}>Manage who can access Creator Commandos</div>
              <AdminPanel currentUser={user}/>
              <ChannelOAuthSection channels={ch.channels || []} />
              <PlatformConnections channels={ch.channels || []} />
            </div>
          ) : !active && tab !== "quota" ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"70vh", gap:0, padding:"40px 20px", textAlign:"center" }}>
              <LogoMark size={80}/>
              <div style={{ marginTop:24, marginBottom:8, fontFamily:"Impact,'Arial Black',sans-serif", fontSize:26, textTransform:"uppercase", letterSpacing:"0.1em", color:"#E8EBF0" }}>
                Welcome to Creator Commandos
              </div>
              <div style={{ fontSize:13, color:"#5A6A7E", marginBottom:32, maxWidth:400, lineHeight:1.7 }}>
                {isAdmin
                  ? "Connect your YouTube channel to get started. Your full video library loads automatically."
                  : "Your account is ready. Ask your admin to assign a channel to your account."}
              </div>
              {isAdmin && (
                <>
                  <button onClick={() => setShowAdd(true)}
                    style={{ padding:"13px 32px", background:"#C9A227", border:"none", borderRadius:4, color:"#080C12", fontFamily:"inherit", fontSize:12, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.14em", cursor:"pointer", marginBottom:32 }}>
                    + Connect Your First Channel
                  </button>
                  {/* Quick start checklist */}
                  <div style={{ width:"100%", maxWidth:420, background:"#0D1825", border:"1px solid #1A2940", borderRadius:8, padding:20, textAlign:"left" }}>
                    <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.14em", color:"#5A6A7E", marginBottom:14 }}>
                      Quick start — 4 steps
                    </div>
                    {[
                      { done:false, label:"Connect your YouTube channel", sub:"Need an API key? Get one free at console.cloud.google.com" },
                      { done:false, label:"Fetch your video library", sub:"Load 50–500 videos in one click" },
                      { done:false, label:"Run AI classification", sub:"Classify every episode automatically" },
                      { done:false, label:"Generate your first clip suggestions", sub:"Open any episode → Clips tab → AI Suggest" },
                    ].map((item, i) => (
                      <div key={i} style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:12 }}>
                        <div style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${item.done?"#34d399":"#1A2940"}`, background:item.done?"#34d399":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
                          {item.done && <span style={{ color:"#080C12", fontSize:12, fontWeight:700 }}>✓</span>}
                          {!item.done && <span style={{ color:"#3A4A5E", fontSize:10, fontWeight:700 }}>{i+1}</span>}
                        </div>
                        <div>
                          <div style={{ fontSize:13, color:"#E8EBF0", fontWeight:600, marginBottom:2 }}>{item.label}</div>
                          <div style={{ fontSize:11, color:"#5A6A7E" }}>{item.sub}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {tab==="overview"       && (
                <div>
                  <Overview {...tabProps} isAdmin={isAdmin} setFetchConfirm={isAdmin ? setFetchConfirm : null}/>
                  <div style={{marginTop:24,background:"#0D1825",border:"1px solid #1A2940",borderRadius:8,padding:20}}>
                    <ActivityFeed channelId={ch.activeId} currentUser={user}/>
                  </div>
                </div>
              )}
              {tab==="episodes"       && <Episodes {...tabProps} setFetchConfirm={isAdmin?setFetchConfirm:null}/>}
              {tab==="clips"          && <ClipTracker {...tabProps}/>}
              {tab==="clip-jobs"      && <ClipJobStatus user={user}/>}
              {tab==="platform"       && <Platform {...tabProps}/>}
              {tab==="branding"       && <Branding {...tabProps}/>}
              {tab==="branding-guide" && <BrandingGuide ch={ch}/>}
              {tab==="classify" && (
                <div>
                  <div style={{display:"flex",gap:4,marginBottom:20}}>
                    {[["review","Review & Classify"],["settings","Classification Types"]].map(([v,l]) => (
                      <button key={v} onClick={() => setClassifySubTab(v)}
                        style={{padding:"7px 16px",background:classifySubTab===v?"#1A2940":"transparent",border:"1px solid",borderColor:classifySubTab===v?"#2B5EA7":"#1A2940",borderRadius:6,color:classifySubTab===v?"#E8EBF0":"#5A6A7E",fontSize:12,cursor:"pointer",fontFamily:"inherit",fontWeight:classifySubTab===v?600:400}}>
                        {l}
                      </button>
                    ))}
                  </div>
                  {classifySubTab === "review" && <ClassifyReview ch={ch} />}
                  {classifySubTab === "settings" && <ClassificationSettings ch={ch} />}
                </div>
              )}
              {tab==="billing"        && <BillingPanel userRole={ch.userRole} />}
              {tab==="analytics"      && <AnalyticsDashboard ch={ch}/>}
              {tab==="calendar"       && <ContentCalendar ch={ch} setDetail={setDetail} setTab={setTab}/>}
              {tab==="connections"    && <Connections ch={ch} user={user}/>}
              {tab==="settings"       && <ChannelDefaults user={user}/>}
              {tab==="feedback-admin" && isAdmin && <AdminFeedback/>}
              {tab==="sponsors"       && <SponsorshipTracker ch={ch}/>}
              {tab==="quota"          && isAdmin && <QuotaDashboard/>}
            </>
          )}
          </Suspense>
        </div>
      </div>
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
      {upgradeReason && <UpgradeModal reason={upgradeReason} onClose={() => setUpgradeReason(null)} />}
      <OnboardingModal onComplete={() => { if (isAdmin && ch.channels.length === 0) setShowAdd(true); }} />
    </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/terms" element={<Suspense fallback={<TabFallback />}><TermsPage /></Suspense>} />
        <Route path="/privacy" element={<Suspense fallback={<TabFallback />}><PrivacyPage /></Suspense>} />
        <Route path="*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
}
